import React from 'react'
import MuiTextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Range } from 'rc-slider'
import 'rc-slider/assets/index.css'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    DisplayItemBase,
} from '@/feature/DisplayItems/BaseDisplayItem'

/* ---------- Types ---------- */

type SliderSearchValue = {
    custom: 'slider'
    min: number
    max: number
}

type CalcValue = string | SliderSearchValue | number[] | null

interface CalcSettings extends Record<string, unknown> {
    show_slider?: boolean
    max_slider?: number
}

interface CalcItem extends DisplayItemBase {
    settings?: CalcSettings
}

interface Props extends BaseDisplayItemProps<CalcValue, CalcItem> {
    onKeyPress?: (
        id: string | number,
        e: React.KeyboardEvent<HTMLInputElement>
    ) => void
}

/* ---------- Component ---------- */

class CalcField extends BaseDisplayItem<CalcValue, CalcItem, HTMLInputElement> {
    constructor(props: Props) {
        super(props)

        this.state = {
            value: props.value ?? null,
        }

        this.onSliderChange = this.onSliderChange.bind(this)
    }

    private onSliderChange(value: number[]) {
        this.setState({ value })

        const searchValue: SliderSearchValue = {
            custom: 'slider',
            min: value[0],
            max: value[1],
        }

        const props = this.props as Props

        // preserve legacy behavior
        if (value[0] === 0 && value[1] === 1) {
            props.changeValue?.(props.item.id, null, props.item)
            return
        }

        props.changeValue?.(props.item.id, searchValue, props.item)
    }

    /* ---------- Render: SHOW ---------- */

    renderShow(): React.ReactNode {
        if (!this.state.value) return null
        return this.state.value as any
    }

    /* ---------- Render: EDIT ---------- */
    // Not used in legacy behavior; keep consistent with old component (only search had UI)
    renderEdit(): React.ReactNode {
        return this.renderShow()
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        const props = this.props as Props
        const maxSlider = props.item.settings?.max_slider ?? 1000

        if (props.item.settings?.show_slider) {
            const isRange = Array.isArray(this.state.value)
            const rangeValue = isRange ? (this.state.value as number[]) : [0, 0]

            return (
                <Box>
                    {isRange ? (
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{ mb: 1 }}
                        >
                            {rangeValue[0]} - {rangeValue[1]}
                        </Typography>
                    ) : null}

                    <Range
                        allowCross={false}
                        min={0}
                        max={maxSlider}
                        defaultValue={[0, 0]}
                        onChange={this.onSliderChange}
                    />
                </Box>
            )
        }

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={props.value ?? ''}
                onChange={(e) =>
                    props.changeValue?.(
                        props.item.id,
                        e.target.value,
                        props.item
                    )
                }
                onKeyPress={
                    props.onKeyPress
                        ? (e) => props.onKeyPress!(props.item.id, e)
                        : undefined
                }
            />
        )
    }
}

export default CalcField
